import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import { HALL_FORM_ACCENT } from '../utils/hallDrawerStyles';

function HallPickerCard({
  hall,
  selected = false,
  disabled = false,
  isDark = false,
  onSelect,
}) {
  const imageUrl = hall?.thumbnail || hall?.hall_image_url || hall?.hall_images?.[0]?.url || '';
  const capacity = Number(hall?.capacity || 0);
  const code = hall?.hall_code || hall?.code || '';

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
        opacity: disabled ? 0.55 : 1,
        border: selected
          ? `2px solid ${HALL_FORM_ACCENT}`
          : `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
        background: isDark
          ? `linear-gradient(165deg, ${alpha('#111827', 0.98)} 0%, ${alpha('#0f172a', 0.96)} 100%)`
          : `linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)`,
        boxShadow: selected
          ? `0 12px 28px ${alpha(HALL_FORM_ACCENT, isDark ? 0.28 : 0.22)}`
          : isDark
            ? `0 8px 20px ${alpha('#000', 0.28)}`
            : `0 10px 24px ${alpha('#0f172a', 0.07)}`,
        transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease',
        '&:hover': disabled
          ? {}
          : {
              transform: 'translateY(-3px)',
              borderColor: alpha(HALL_FORM_ACCENT, selected ? 1 : 0.55),
              boxShadow: `0 16px 32px ${alpha(HALL_FORM_ACCENT, isDark ? 0.26 : 0.18)}`,
            },
      }}
    >
      <CardActionArea
        disabled={disabled}
        onClick={() => onSelect?.(hall)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          '&.Mui-disabled': { opacity: 1 },
        }}
      >
        <Box sx={{ position: 'relative', height: 118, flexShrink: 0, overflow: 'hidden' }}>
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={hall?.name || 'Hall'}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.35s ease',
                '.MuiCard-root:hover &': disabled ? {} : { transform: 'scale(1.05)' },
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                background: `linear-gradient(135deg, ${alpha(HALL_FORM_ACCENT, 0.28)} 0%, ${alpha('#0f172a', isDark ? 0.55 : 0.12)} 100%)`,
              }}
            >
              <MapsHomeWorkIcon sx={{ fontSize: 36, color: HALL_FORM_ACCENT, opacity: 0.75 }} />
            </Box>
          )}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `
                linear-gradient(180deg, ${alpha('#000', 0.05)} 0%, ${alpha('#000', 0.08)} 45%, ${alpha('#020617', 0.55)} 100%),
                radial-gradient(70% 55% at 100% 0%, ${alpha(HALL_FORM_ACCENT, 0.22)} 0%, transparent 60%)
              `,
              pointerEvents: 'none',
            }}
          />

          {code ? (
            <Chip
              size="small"
              label={code}
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                height: 22,
                fontWeight: 800,
                fontSize: '0.68rem',
                color: '#04120e',
                bgcolor: alpha(HALL_FORM_ACCENT, 0.92),
                backdropFilter: 'blur(8px)',
              }}
            />
          ) : null}

          {selected ? (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 26,
                height: 26,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: HALL_FORM_ACCENT,
                color: '#04120e',
                boxShadow: `0 6px 14px ${alpha('#000', 0.35)}`,
              }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            px: 1.15,
            py: 1.05,
            minHeight: 58,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.35,
            borderTop: `1px solid ${
              selected
                ? alpha(HALL_FORM_ACCENT, 0.35)
                : isDark
                  ? alpha(HALL_FORM_ACCENT, 0.16)
                  : alpha(HALL_FORM_ACCENT, 0.12)
            }`,
            background: isDark
              ? alpha('#0b1220', 0.92)
              : alpha('#ffffff', 0.96),
          }}
        >
          <Typography
            variant="subtitle2"
            noWrap
            title={hall?.name || 'Hall'}
            sx={{
              fontWeight: 850,
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
              color: selected
                ? (isDark ? HALL_FORM_ACCENT : '#0f766e')
                : (isDark ? '#f8fafc' : '#0f172a'),
            }}
          >
            {hall?.name || 'Hall'}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            {capacity > 0 ? (
              <Stack direction="row" spacing={0.35} alignItems="center" sx={{ minWidth: 0 }}>
                <GroupsOutlinedIcon
                  sx={{
                    fontSize: 14,
                    color: isDark ? '#94a3b8' : '#64748b',
                  }}
                />
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 650 }}
                >
                  {capacity} guests
                </Typography>
              </Stack>
            ) : (
              <Typography
                variant="caption"
                noWrap
                sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 650 }}
              >
                {hall?.business_type_label || hall?.hall_type || 'Banquet'}
              </Typography>
            )}
            {selected ? (
              <Chip
                size="small"
                label="Selected"
                sx={{
                  ml: 'auto',
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  color: isDark ? '#04120e' : '#065f46',
                  bgcolor: alpha(HALL_FORM_ACCENT, isDark ? 0.9 : 0.22),
                }}
              />
            ) : null}
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
}

/**
 * Single-select banquet/hall picker — image cards with name in the footer.
 */
export default function HallPickerCards({
  halls = [],
  value = '',
  onChange,
  isDark = false,
  disabled = false,
  helperText,
  emptyText = 'No active halls available. Activate a hall first.',
}) {
  const selectedId = value != null ? String(value) : '';

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.85,
          fontWeight: 750,
          letterSpacing: '0.02em',
          color: isDark ? '#94a3b8' : '#64748b',
        }}
      >
        Hall *
      </Typography>

      {halls.length === 0 ? (
        <Box
          sx={{
            borderRadius: '14px',
            px: 1.5,
            py: 2,
            textAlign: 'center',
            border: `1px dashed ${isDark ? alpha('#fff', 0.16) : alpha('#0f172a', 0.14)}`,
            bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#0f172a', 0.02),
          }}
        >
          <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            {emptyText}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: halls.length > 2 ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
            },
            gap: 1.25,
          }}
        >
          {halls.map((hall) => {
            const hallId = String(hall.id);
            return (
              <HallPickerCard
                key={hall.id}
                hall={hall}
                selected={hallId === selectedId}
                disabled={disabled}
                isDark={isDark}
                onSelect={(nextHall) => {
                  if (disabled) return;
                  onChange?.(String(nextHall.id), nextHall);
                }}
              />
            );
          })}
        </Box>
      )}

      {helperText ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.85,
            color: isDark ? '#94a3b8' : '#64748b',
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}
